import { useState, type ComponentProps } from 'react'
import { Languages, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { describeTranslateError, translateFromRussian } from '@/lib/translate'
import { cn } from '@/lib/utils'

/**
 * A translation field (`en` / `az`) with a small «Вставить перевод» button: it machine-translates
 * the Russian base text ([source]) and puts the result into the field (replacing what is there).
 * The admin reviews it before saving. See `lib/translate.ts` for the service and its limits.
 */
type TranslateProps = {
  /** The Russian base text to translate. The button is disabled while it is blank. */
  source: string
  /** Target language tag ('en', 'az'). */
  lang: string
  /** Receives the translation. Use a functional state update: two languages may finish in any order. */
  onTranslated: (text: string) => void
}

export function TranslatedInput({
  source,
  lang,
  onTranslated,
  className,
  ...props
}: ComponentProps<'input'> & TranslateProps) {
  const translate = useTranslate(source, lang, onTranslated)
  return (
    <div className="grid gap-1">
      <div className="relative">
        <Input {...props} className={cn('pr-9', className)} />
        <TranslateButton {...translate} className="absolute top-1/2 right-1 -translate-y-1/2" />
      </div>
      <TranslateError error={translate.error} />
    </div>
  )
}

export function TranslatedTextarea({
  source,
  lang,
  onTranslated,
  className,
  ...props
}: ComponentProps<'textarea'> & TranslateProps) {
  const translate = useTranslate(source, lang, onTranslated)
  return (
    <div className="grid gap-1">
      <div className="relative">
        <Textarea {...props} className={cn('pr-9', className)} />
        <TranslateButton {...translate} className="absolute top-1 right-1" />
      </div>
      <TranslateError error={translate.error} />
    </div>
  )
}

function useTranslate(source: string, lang: string, onTranslated: (text: string) => void) {
  const [isBusy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const canTranslate = source.trim().length > 0

  const run = async () => {
    if (!canTranslate || isBusy) return
    setBusy(true)
    setError(null)
    try {
      onTranslated(await translateFromRussian(source, lang))
    } catch (caught) {
      setError(describeTranslateError(caught))
    } finally {
      setBusy(false)
    }
  }

  return { lang, canTranslate, isBusy, error, run }
}

function TranslateButton({
  lang,
  canTranslate,
  isBusy,
  error,
  run,
  className,
}: ReturnType<typeof useTranslate> & { className?: string }) {
  const label = canTranslate
    ? `Вставить перевод с русского (${lang.toUpperCase()}, Google Translate) — заменит текст поля`
    : 'Сначала заполните русский текст'
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn('text-muted-foreground size-7', error ? 'text-destructive' : null, className)}
      disabled={!canTranslate || isBusy}
      onClick={() => void run()}
      title={label}
      aria-label={label}
    >
      {isBusy ? <Loader2 className="animate-spin" /> : <Languages />}
    </Button>
  )
}

function TranslateError({ error }: { error: string | null }) {
  return error ? (
    <p role="alert" className="text-destructive text-xs">
      {error}
    </p>
  ) : null
}
